import { Button } from '@otter-ai/den-components';
import { getDesktopAppUtmContent } from '@otter-ai/desktop';
import {
  CheckoutEntryPoint,
  LimitStatus,
  type PremiumFeature,
  PurchaseOpenAccountPlanUpgradeEvent,
  trackEvent,
  type UpgradePlanType,
} from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { PATH_URL, SETTINGS_PATHS } from '@/constants/path-constants';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { usePaywallActions } from '@/shared/modals/checkout-dialog';
import { OtterPlanType } from '@/shared/types/plan';
import { CRM_PAYWALL_TEST_IDS } from '../test-ids/crm-paywall';
import { trackCRM } from '../tracking/crm-tracking';

type CrmUpgradeButtonProps = {
  service: 'salesforce' | 'hubspot';
  limitReached: boolean;
  connectionsUsage: number;
};

export function CrmUpgradeButton({
  service,
  limitReached,
  connectionsUsage,
}: CrmUpgradeButtonProps) {
  const t = useTranslations(`settings:integrations.apps.${service}`);
  const { data: subscription } = useQuery(subscriptionQuery());
  const { getOneClickCheckoutConfig, tryOpenPaywallDialog } =
    usePaywallActions();

  const isCCTrialsEnabled = useExperimentEnabled('cc_trials_post_onboarding');

  // Determine upgrade plan type based on current subscription
  const upgradePlanType = useMemo(() => {
    if (subscription?.plan.type === OtterPlanType.PREMIUM) {
      return OtterPlanType.TEAM;
    }
    return OtterPlanType.PREMIUM;
  }, [subscription?.plan.type]);

  const config = getOneClickCheckoutConfig(upgradePlanType);

  const shouldOpenPlansPage =
    subscription?.plan.type === 'free' &&
    !isCCTrialsEnabled &&
    !config.showReactivateText;

  const utmContent = getDesktopAppUtmContent();
  const href = shouldOpenPlansPage
    ? `${SETTINGS_PATHS.SETTINGS_PLANS}#crm_connections`
    : upgradePlanType === OtterPlanType.PREMIUM
      ? `${PATH_URL.PURCHASE_PRO_PLAN}?utm_content=${encodeURIComponent(utmContent)}`
      : `${PATH_URL.PURCHASE_TEAM_PLAN}?utm_content=${encodeURIComponent(utmContent)}`;

  const getButtonText = () => {
    if (config.dialogType === 'cc-trials') return t('tryForFreeButton');
    if (config.showReactivateText) return t('reactivateButton');
    return t('upgradeButton');
  };

  const premiumFeature = (
    service === 'salesforce' ? 'crmSalesforce' : 'crmHubspot'
  ) as PremiumFeature;

  const handleClick = (e: React.MouseEvent) => {
    trackCRM.paywall.clickUpgrade(service);

    trackEvent(
      PurchaseOpenAccountPlanUpgradeEvent({
        UpgradePlanType: config.paramsToUse.planType as UpgradePlanType,
        PremiumFeature: premiumFeature,
        LimitStatus: limitReached
          ? LimitStatus.limitReached
          : LimitStatus.limitApproaching,
        LimitUsage: connectionsUsage,
      }),
    );

    if (config.dialogType !== 'none') {
      const opened = tryOpenPaywallDialog({
        config,
        premiumFeature,
        checkoutEntryPoint: CheckoutEntryPoint.paywall,
      });
      if (opened) {
        e.preventDefault();
      }
    }
  };

  return (
    <Button asChild data-testid={CRM_PAYWALL_TEST_IDS.upgradeButton(service)}>
      <Link
        href={href}
        target={shouldOpenPlansPage ? undefined : '_blank'}
        rel={shouldOpenPlansPage ? undefined : 'noopener noreferrer'}
        onClick={handleClick}
      >
        {getButtonText()}
      </Link>
    </Button>
  );
}
